import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Star, CalendarDays, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/Stars";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" onClick={() => onChange(i)} data-testid={`review-star-${i}`} className="transition-transform hover:scale-110">
          <Star className={`h-7 w-7 ${value >= i ? "fill-[#D97706] text-[#D97706]" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ bid, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [date, setDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Choisissez une note");
    setBusy(true);
    try {
      const invitation = new URLSearchParams(window.location.search).get("invitation");
      await api.post(`/businesses/${bid}/reviews`, { rating, comment, intervention_date: date ? format(date, "yyyy-MM-dd") : null, invitation });
      toast.success("Merci pour votre avis !");
      setRating(0); setComment(""); setDate(null);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="review-form">
      <p className="font-semibold">Cette entreprise est intervenue chez vous ? Notez-la</p>
      <StarPicker value={rating} onChange={setRating} />
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Décrivez l'intervention : ponctualité, qualité, prix…" required minLength={3} className="bg-white" data-testid="review-comment-input" />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600">Date de l'intervention</label>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="gap-2 bg-white font-normal" data-testid="review-date-input">
              <CalendarDays className="h-4 w-4 text-slate-500" />{date ? format(date, "dd/MM/yyyy") : "jj/mm/aaaa"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" locale={fr} weekStartsOn={1} selected={date || undefined} onSelect={(d) => setDate(d || null)}
              disabled={{ after: new Date() }} initialFocus data-testid="review-date-calendar" />
          </PopoverContent>
        </Popover>
        <Button type="submit" disabled={busy} className="ml-auto bg-[#1E40AF] hover:bg-[#1D4ED8] hover:text-white" data-testid="review-submit-button">Publier mon avis</Button>
      </div>
    </form>
  );
}

export function Reviews({ b, onChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const load = () => api.get(`/businesses/${b.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  const refresh = () => {
    load();
    onChange?.();
  };
  useEffect(() => {
    load();
  }, [b.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section id="avis" className="space-y-5" data-testid="reviews-section">
      <h2 className="font-display text-2xl font-bold">Avis de la communauté <span className="text-slate-400">({reviews.length})</span></h2>
      {user ? (!b.is_owner && <ReviewForm bid={b.id} onDone={refresh} />) : (
        <Link to={`/connexion?next=${encodeURIComponent(`/entreprise/${b.id}${window.location.search.includes("noter=1") ? window.location.search : "?noter=1"}`)}`} className="block rounded-xl border border-dashed border-slate-300 bg-white p-5 text-center text-sm font-semibold text-[#1E40AF] hover:bg-[#EFF6FF]" data-testid="review-login-prompt">
          Connectez-vous pour noter cette entreprise
        </Link>
      )}
      {reviews.map((r) => (
        <article key={r.id} className={`rounded-xl border bg-white p-5 ${r.verified ? "border-[#86EFAC]" : "border-slate-200"}`} data-testid={`review-${r.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2 font-semibold">{r.author}
              {r.verified && <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-xs font-semibold text-[#14532D]" data-testid={`verified-badge-${r.id}`}><ShieldCheck className="h-3 w-3" />Avis vérifié</span>}
            </span>
            <Stars value={r.rating} />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {new Date(r.created_at).toLocaleDateString("fr-FR")}{r.intervention_date ? ` · intervention le ${new Date(r.intervention_date).toLocaleDateString("fr-FR")}` : ""}
          </p>
          <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{r.comment}</p>
          {r.reply && (
            <div className="mt-4 rounded-lg border-l-4 border-[#1E40AF] bg-[#EFF6FF] p-3 text-sm text-slate-700">
              <p className="mb-1 flex items-center gap-1 text-xs font-bold text-[#1E40AF]"><MessageSquare className="h-3 w-3" />Réponse du professionnel</p>{r.reply}
            </div>
          )}
        </article>
      ))}
    </section>
  );
}
